/**
 * InfinitSales - Card "usou ferramentas" na thread (observabilidade de tools)
 *
 * O que o agente FEZ num turno, dentro do próprio inbox — colapsado por padrão
 * ("3 ferramentas"), expandir mostra a lista com veredito (✓/✗) e o resumo dos
 * argumentos. Também cobre os cards do Gestor:
 *   kind "gestor"        → "Gestor acionado — para a equipe: <mensagem>"
 *   kind "gestor_reply"  → "Retorno pedido pela equipe (via Gestor)"
 *   kind "agent_error"   → "O agente não conseguiu responder" (turno perdido
 *                          por timeout/erro da IA: o operador vê no inbox em
 *                          vez do lead ficar sem resposta em silêncio)
 *   kind "first_contact" → "Primeiro contato no WhatsApp" (o lead veio do
 *                          Instagram/Messenger, passou o número e a tool
 *                          cadastrar_lead chamou por lá; sem este card o
 *                          atendimento some da conversa de origem e o
 *                          operador não sabe se alguém falou com o lead)
 *
 * Mesmo estilo do ThreadEventCard (aviso de sistema, sem competir com as
 * bolhas), usado pelos 4 inboxes; cores só dos tokens do tema.
 *
 * O dado vem do doc `tool_event` gravado no container de mensagens do canal
 * (backend: services/agent_tool_observability.py, flag AGENT_TOOL_CARDS_ENABLED).
 */
import React, { useState } from 'react';
import {
  Wrench, ChevronDown, ChevronUp, Check, X, Megaphone, CornerDownLeft,
  AlertTriangle, Send,
} from 'lucide-react';

const formatEventTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const ToolUsageCard = ({ toolEvent, agentName, timestamp, colors }) => {
  const [open, setOpen] = useState(false);
  if (!toolEvent) return null;

  const kind = toolEvent.kind || 'tools';
  const tools = toolEvent.tools || [];

  const styles = {
    wrapper: { display: 'flex', justifyContent: 'center', margin: '12px 0' },
    card: {
      maxWidth: '70%', minWidth: 0,
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: '7px 12px', borderRadius: 8,
      backgroundColor: colors.bgTertiary,
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    headline: {
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 12, lineHeight: 1.3,
      cursor: kind === 'tools' ? 'pointer' : 'default',
      background: 'none', border: 'none', padding: 0,
      color: 'inherit', textAlign: 'left', width: '100%',
    },
    strong: { color: colors.textPrimary, fontWeight: 500, overflowWrap: 'anywhere' },
    meta: { color: colors.textTertiary, whiteSpace: 'nowrap', marginLeft: 'auto' },
    row: {
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11.5, lineHeight: 1.4,
    },
    text: {
      fontSize: 12, lineHeight: 1.4, color: colors.textSecondary,
      overflowWrap: 'anywhere', margin: 0,
    },
  };

  if (kind === 'agent_error') {
    return (
      <div style={styles.wrapper}>
        <div style={{ ...styles.card, border: '1px solid #DC262633' }}>
          <div style={{ ...styles.headline, cursor: 'default' }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, color: '#DC2626' }} />
            <span style={styles.strong}>
              {agentName ? `${agentName} não conseguiu responder` : 'O agente não conseguiu responder'}
            </span>
            <span style={styles.meta}>{formatEventTime(timestamp)}</span>
          </div>
          {toolEvent.text && <p style={styles.text}>{toolEvent.text}</p>}
          <p style={{ ...styles.text, color: colors.textTertiary }}>
            A mensagem do cliente continua aguardando: responda por aqui ou peça para o agente tentar de novo.
          </p>
        </div>
      </div>
    );
  }

  if (kind === 'first_contact') {
    // Enviado e não enviado são estados MUITO diferentes para quem atende: um
    // diz "pode encerrar por aqui", o outro diz "ninguém falou com esse lead".
    const enviado = Boolean(toolEvent.enviado);
    return (
      <div style={styles.wrapper}>
        <div style={{
          ...styles.card,
          ...(enviado ? {} : { border: '1px solid #DC262633' }),
        }}>
          <div style={{ ...styles.headline, cursor: 'default' }}>
            <Send
              size={14}
              style={{ flexShrink: 0, color: enviado ? colors.textTertiary : '#DC2626' }}
            />
            <span style={styles.strong}>
              {enviado
                ? 'Primeiro contato enviado no WhatsApp'
                : 'Primeiro contato no WhatsApp NÃO saiu'}
            </span>
            <span style={styles.meta}>{formatEventTime(timestamp)}</span>
          </div>
          {toolEvent.telefone && (
            <p style={styles.text}>Número: {toolEvent.telefone}</p>
          )}
          {enviado && toolEvent.template && (
            <p style={styles.text}>Template: {toolEvent.template}</p>
          )}
          {enviado && toolEvent.text && (
            <p style={styles.text}>{toolEvent.text}</p>
          )}
          {!enviado && (
            <p style={{ ...styles.text, color: colors.textTertiary }}>
              {toolEvent.motivo || 'Motivo não informado.'} Chame o lead você
              mesmo ou siga o atendimento por aqui.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (kind === 'gestor' || kind === 'gestor_reply') {
    const Icon = kind === 'gestor' ? Megaphone : CornerDownLeft;
    const title = kind === 'gestor'
      ? `Gestor acionado${toolEvent.member_name ? ` — avisou ${toolEvent.member_name}` : ''}`
      : 'Retorno pedido pela equipe (via Gestor)';
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={{ ...styles.headline, cursor: 'default' }}>
            <Icon size={14} style={{ flexShrink: 0, color: colors.textTertiary }} />
            <span style={styles.strong}>{title}</span>
            <span style={styles.meta}>{formatEventTime(timestamp)}</span>
          </div>
          {toolEvent.text && (
            <p style={styles.text}>
              {kind === 'gestor' ? 'Para a equipe: ' : 'Pedido: '}
              {toolEvent.text}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <button
          type="button"
          style={styles.headline}
          onClick={() => setOpen((v) => !v)}
        >
          <Wrench size={13} style={{ flexShrink: 0, color: colors.textTertiary }} />
          <span style={styles.strong}>
            {agentName ? `${agentName} usou ` : 'Usou '}
            {tools.length} ferramenta{tools.length === 1 ? '' : 's'}
          </span>
          {tools.some((t) => t.ok === false) && (
            <X size={12} color="#DC2626" style={{ flexShrink: 0 }} />
          )}
          <span style={styles.meta}>{formatEventTime(timestamp)}</span>
          {open
            ? <ChevronUp size={13} style={{ flexShrink: 0, color: colors.textTertiary }} />
            : <ChevronDown size={13} style={{ flexShrink: 0, color: colors.textTertiary }} />}
        </button>

        {open && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {tools.map((t, i) => (
              <div key={`${t.name}-${i}`} style={styles.row}>
                {t.ok === false
                  ? <X size={12} color="#DC2626" style={{ flexShrink: 0, marginTop: 2 }} />
                  : <Check size={12} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />}
                <span style={{ overflowWrap: 'anywhere' }}>
                  <span style={{ color: colors.textPrimary, fontWeight: 500 }}>
                    {t.name}
                  </span>
                  {t.args ? ` (${t.args})` : ''}
                  {t.ok === false && t.error ? (
                    <span style={{ color: '#DC2626' }}> — {t.error}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolUsageCard;
